/**
 * The system sky (spec §8; style guide §1.1, §3.3): the seeded nebula baked at load into a background cubemap
 * (1024² / 512² / 256² per face by preset) and a small cube that PMREM turns into `scene.environment`, plus the
 * live star layer. Replaces the M0 live sky shader (D-021). Asset-lane owned (task A8).
 * The background is exposed as a node (`scene.backgroundNode`, the WebGPU form of `scene.background`) whose
 * texture is swapped in place when the preset re-bakes, so nothing holds a stale texture.
 * M3 (R3): `reseed` turns the same sky into another star system's: new seed uniforms in the compiled nebula (no
 * recompile), a regenerated star catalogue written into the existing buffers, then both cubes and the PMREM re-bake.
 */
import type { CubeRenderTarget, Mesh, Node, RenderTarget, Texture, WebGPURenderer } from 'three/webgpu'
import { cubeTexture, vec4 } from 'three/tsl'
import { logger } from '../../core/logger'
import type { QualityPreset } from '../contracts'
import { bakeSizeFor, unitDirection, type EnvironmentFile } from './data'
import { createSkyBaker, gpuIdle } from './sky/bake'
import { createNebulaShader, type NebulaConfig } from './sky/nebula'
import { createStarLayer } from './sky/stars'
import { bandNormalFor, generateStars } from './sky/starfield'

export interface SkyStats {
  /** Face size of the background cube. */
  bakeSize: number
  /** Wall time of the last bake from the first render call to GPU idle (WebGPU), in ms. */
  bakeMs: number
}

export interface Sky {
  readonly stars: Mesh
  /** vec4 background colour node for `scene.backgroundNode`. */
  readonly backgroundNode: Node<'vec4'>
  /** The PMREM texture for `scene.environment`. */
  readonly environment: Texture
  readonly stats: SkyStats
  readonly starBrightness: { value: number }
  /** Re-bakes the background at the preset's face size (no-op when unchanged). */
  setPreset(preset: QualityPreset): void
  /** Pushes new nebula values and re-bakes both cubes (dev tuning). */
  rebake(cfg: NebulaConfig): void
  /**
   * Another system's sky: re-seeds the nebula and the star catalogue, pushes `cfg` and re-bakes both cubes and the
   * PMREM. Resolves after GPU idle with the measured bake time (ms), also stored in `stats.bakeMs`.
   */
  reseed(seed: number, cfg: NebulaConfig): Promise<number>
  dispose(): void
}

export async function createSky(renderer: WebGPURenderer, env: EnvironmentFile, seed: number, preset: QualityPreset): Promise<Sky> {
  const lobe = unitDirection(env.lights.rim.direction)
  const bandNormal = bandNormalFor(seed, lobe)
  const nebula = createNebulaShader(env.sky.nebula, seed, lobe, bandNormal)
  const baker = createSkyBaker(renderer, nebula.node)
  const bake = (size: number): CubeRenderTarget => {
    nebula.setFaceSize(size)
    return baker.bakeCube(size)
  }
  const stats: SkyStats = { bakeSize: bakeSizeFor(env, preset), bakeMs: 0 }

  const t0 = performance.now()
  let background: CubeRenderTarget = bake(stats.bakeSize)
  const t1 = performance.now()
  const envCube = bake(env.sky.envSize)
  const t2 = performance.now()
  const pmrem: RenderTarget = baker.pmrem(envCube)
  const t3 = performance.now()
  await gpuIdle(renderer)
  stats.bakeMs = performance.now() - t0
  logger.debug('Sky bake breakdown (ms)', { background: t1 - t0, envCube: t2 - t1, pmrem: t3 - t2, gpu: stats.bakeMs - (t3 - t0) })

  const bgTex = cubeTexture(background.texture)
  const field = generateStars(env.sky.stars, seed, bandNormal, env.sky.nebula.bandWidthDeg)
  const stars = createStarLayer(field, env.sky.radius, envCube.texture, env.sky.stars.dustDimming)

  /** Pushes `cfg` and re-bakes the background and the env cube (in place: the star layer samples its dust), then PMREM. */
  const rebakeAll = (cfg: NebulaConfig): number => {
    nebula.set(cfg)
    const start = performance.now()
    const next = bake(stats.bakeSize)
    bgTex.value = next.texture
    background.dispose()
    background = next
    nebula.setFaceSize(env.sky.envSize)
    baker.bakeInto(envCube)
    baker.pmrem(envCube)
    return start
  }

  const timeBake = (start: number): void => {
    void gpuIdle(renderer).then(() => {
      stats.bakeMs = performance.now() - start
    })
  }

  return {
    stars: stars.mesh,
    backgroundNode: vec4(bgTex.rgb, 1),
    environment: pmrem.texture,
    stats,
    starBrightness: stars.brightness,
    setPreset(p: QualityPreset): void {
      const size = bakeSizeFor(env, p)
      if (size === stats.bakeSize) return
      const start = performance.now()
      const next = bake(size)
      bgTex.value = next.texture
      background.dispose()
      background = next
      stats.bakeSize = size
      timeBake(start)
    },
    rebake(cfg: NebulaConfig): void {
      timeBake(rebakeAll(cfg))
    },
    async reseed(nextSeed: number, cfg: NebulaConfig): Promise<number> {
      const start = performance.now()
      const nextBand = bandNormalFor(nextSeed, lobe)
      nebula.reseed(nextSeed, lobe, nextBand)
      stars.setField(generateStars(env.sky.stars, nextSeed, nextBand, cfg.bandWidthDeg))
      rebakeAll(cfg)
      await gpuIdle(renderer)
      stats.bakeMs = performance.now() - start
      return stats.bakeMs
    },
    dispose(): void {
      stars.dispose()
      background.dispose()
      envCube.dispose()
      baker.dispose()
    },
  }
}
